<template>
    <div class="center-map">
      <div class="map-stats">
        <div v-for="stat in mapStats" :key="stat.label" class="stat-card">
          <div class="stat-label">{{ stat.label }}</div>
          <div class="stat-value">{{ stat.value }}</div>
          <div class="stat-chart">
            <div class="chart-bar" :style="{ width: stat.percentage + '%' }"></div>
          </div>
        </div>
      </div>

      <div id="amap-container" class="amap-container"></div>

      <!-- 点位悬浮层（楼宇/视频/门禁/车辆/人员） -->
      <div
        v-if="entityPopupVisible && entityPopup"
        class="entity-popup"
        :style="{ left: `${entityPopupPos.x}px`, top: `${entityPopupPos.y}px` }"
        @mousedown.stop
      >
        <button class="popup-close" type="button" @click="closeEntityPopup">×</button>
        <div class="popup-body">
          <button
            class="popup-thumb"
            type="button"
            :class="{ clickable: entityPopup && entityPopup.enableImagePreview }"
            @click="handlePopupThumbClick"
          >
            <img v-if="entityPopup.image" :src="entityPopup.image" alt="" />
            <div v-else class="thumb-placeholder">{{ entityPopup.badge || '信息' }}</div>
          </button>
          <div class="popup-content">
            <div v-if="entityPopup.title" class="popup-head">{{ entityPopup.title }}</div>
            <div v-for="(row, idx) in entityPopup.rows" :key="idx" class="popup-row">
              <span v-for="(item, j) in row" :key="j" class="kv"><b>{{ item.k }}：</b>{{ item.v }}</span>
            </div>
            <div v-if="entityPopup.address" class="popup-address"><b>地址：</b>{{ entityPopup.address }}</div>
          </div>
        </div>
      </div>

      <!-- 图片预览层（车辆/人员：点击缩略图弹出） -->
      <div v-if="imagePreviewVisible" class="image-mask" @click="closeImagePreview">
        <div class="image-modal" @click.stop>
          <div class="image-header">
            <div class="image-title">{{ imagePreviewTitle }}</div>
            <button class="icon-btn" type="button" title="关闭" @click="closeImagePreview">×</button>
          </div>
          <div class="image-body">
            <img v-if="imagePreviewUrl" class="image-el" :src="imagePreviewUrl" alt="" />
            <div v-else class="image-empty">暂无图片</div>
          </div>
        </div>
      </div>

      <!-- 视频播放层 -->
      <div v-if="videoPlayerVisible" class="video-mask" @click="closeVideoPlayer">
        <div ref="videoContainerRef" class="video-modal" @click.stop>
          <div class="video-header">
            <div class="video-title">{{ videoPlayerTitle }}</div>
            <div class="video-actions">
              <button class="icon-btn" type="button" title="全屏" @click="toggleVideoFullscreen">⛶</button>
              <button class="icon-btn" type="button" title="关闭" @click="closeVideoPlayer">×</button>
            </div>
          </div>
          <div class="video-body">
            <video v-if="videoPlayerUrl" class="video-el" :src="videoPlayerUrl" autoplay controls playsinline></video>
            <div v-else class="video-empty">暂无视频流（请在点位配置 `videoUrl`）</div>
          </div>
        </div>
      </div>

      <div class="map-legend">
        <div v-for="layer in layerList" :key="layer.key" class="legend-item" :class="{ active: layer.visible }">
          <input
            type="checkbox"
            class="legend-checkbox"
            :id="`layer-${layer.key}`"
            v-model="layer.visible"
            @change="toggleLayer(layer.key)"
          />
          <label class="legend-label" :for="`layer-${layer.key}`">
            <span class="legend-icon" :class="layer.key">{{ layer.icon }}</span>
            <span class="legend-text">{{ layer.label }}</span>
            <span class="legend-count" v-if="getLayerCount(layer.key) > 0">{{ getLayerCount(layer.key) }}</span>
          </label>
        </div>
      </div>

    </div>
  </template>

  <script>
  import { communityStore } from '../stores/community'
  import { getCommunityConfig, COMMUNITIES } from '../utils/communityConfig'
  import { REFRESH_CONFIG } from '../utils/refreshConfig'
  import request from '@/utils/request'
  import { getCurrentCommunity } from '@/utils/vc'

  export default {
    name: 'CenterMap',
    data() {
      return {
        communityStore,
        mapStats: [],
        entityPopup: null,
        entityPopupVisible: false,
        entityPopupPos: { x: -9999, y: -9999 },
        selectedMarker: null,
        mapMoveHandler: null,
        videoPlayerVisible: false,
        videoPlayerUrl: '',
        videoPlayerTitle: '视频监控',
        imagePreviewVisible: false,
        imagePreviewUrl: '',
        imagePreviewTitle: '图片预览',
        layerList: [
          { key: 'building', label: '楼宇分布', icon: '🏢', visible: true },
          { key: 'video', label: '视频监控', icon: '📹', visible: false },
          { key: 'access', label: '门禁设备', icon: '🚪', visible: false },
          { key: 'vehicle', label: '车辆分布', icon: '🚗', visible: false },
          { key: 'person', label: '人员分布', icon: '👤', visible: false }
        ],
        map: null,
        boundaryPolyline: null,
        communityLabelMarker: null,
        buildingMarkers: [],
        deviceMarkers: [],
        vehicleMarkers: [],
        personMarkers: [],
        refreshTimer: null
      }
    },
    watch: {
      'communityStore.currentCommunity'() {
        this.loadBasicInfoFromAPI()
        this.loadCommunityData()
      },
    },
    mounted() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.initAmap()
        }, 200)
      })
      
      window.addEventListener('resize', this.handleResize)
      
      this.refreshTimer = setInterval(() => {
        this.loadBasicInfoFromAPI()
        this.loadCommunityData()
      }, REFRESH_CONFIG.MAP_DATA)
    },
    beforeDestroy() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
      
      window.removeEventListener('resize', this.handleResize)
      
      if (this.map) {
        this.clearAllMarkers()
        this.map.destroy()
        this.map = null
      }
    },
    methods: {
      initAmap() {
        if (typeof window.AMap === 'undefined') {
          console.error('高德地图 API 未加载')
          return
        }
        
        const container = document.getElementById('amap-container')
        if (!container) {
          console.error('地图容器不存在')
          return
        }
        
        // 初始化高德地图 - 使用深色科技主题
        this.map = new window.AMap.Map('amap-container', {
          zoom: 17, // 初始缩放级别，后续会根据社区边界自动调整
          center: [114.0, 33.0], // 默认中心点，后续会根据配置更新
          mapStyle: 'amap://styles/darkblue', // 使用高德内置深蓝色主题，科技感强
          viewMode: '2D',
          features: ['bg', 'point', 'road', 'building']
        })
        
        // 地图加载完成后加载数据
        this.map.on('complete', () => {
          this.loadBasicInfoFromAPI()
          this.loadCommunityData()
        })
        
        // 绑定地图点击事件
        this.map.on('click', () => {
          this.closeEntityPopup()
          this.closeVideoPlayer()
        })
      },
      clearAllMarkers() {
        // 清除所有标记
        this.buildingMarkers.forEach(marker => {
          this.map.remove(marker)
        })
        this.deviceMarkers.forEach(marker => {
          this.map.remove(marker)
        })
        this.vehicleMarkers.forEach(marker => {
          this.map.remove(marker)
        })
        this.personMarkers.forEach(marker => {
          this.map.remove(marker)
        })
        
        if (this.boundaryPolyline) {
          this.map.remove(this.boundaryPolyline)
          this.boundaryPolyline = null
        }
        
        if (this.communityLabelMarker) {
          this.map.remove(this.communityLabelMarker)
          this.communityLabelMarker = null
        }
        
        this.buildingMarkers = []
        this.deviceMarkers = []
        this.vehicleMarkers = []
        this.personMarkers = []
      },
      loadBasicInfoFromAPI() {
        let communityId = null
        try {
          const currentCommunity = getCurrentCommunity()
          if (currentCommunity && currentCommunity.communityId) {
            communityId = currentCommunity.communityId
          }
        } catch (e) {
          console.warn('获取 communityId 失败:', e)
          return
        }

        if (!communityId) {
          console.warn('communityId 为空，无法获取社区基础信息')
          return
        }

        request({
          url: '/app/bigScreen/getBasicInfo',
          method: 'get',
          params: {
            communityId: communityId
          },
          showLoading: false
        }).then(response => {
          const res = response.data
          if (res && res.code === 0 && res.data) {
            const data = res.data
            const currentCommunityName = this.communityStore.currentCommunity
            const cfg = COMMUNITIES[currentCommunityName]

            if (!cfg || !cfg.data) {
              console.warn('未找到社区配置:', currentCommunityName)
              return
            }

            if (data.vehicle && cfg.data.vehicleStats) {
              cfg.data.vehicleStats.forEach(stat => {
                if (stat.field && data.vehicle[stat.field] !== undefined) {
                  stat.value = data.vehicle[stat.field] || 0
                }
              })
            }

            if (data.house && cfg.data.houseStats) {
              cfg.data.houseStats.forEach(stat => {
                if (stat.field && data.house[stat.field] !== undefined) {
                  stat.value = data.house[stat.field] || 0
                }
              })
            }

            if (data.population && cfg.data.population) {
              cfg.data.population.forEach(stat => {
                if (stat.field && data.population[stat.field] !== undefined) {
                  stat.value = data.population[stat.field] || 0
                }
              })
            }

            if (data.mapStats && cfg.data.mapStats) {
              cfg.data.mapStats.forEach(stat => {
                if (stat.field && data.mapStats[stat.field] !== undefined) {
                  stat.value = data.mapStats[stat.field] || 0
                  if (data.mapStats[stat.field + 'Percentage'] !== undefined) {
                    stat.percentage = data.mapStats[stat.field + 'Percentage'] || 0
                  }
                }
              })
            }

            this.loadCommunityData()
          }
        }).catch(error => {
          console.error('获取社区基础信息失败:', error)
        })
      },
      loadCommunityData() {
        if (!this.map) {
          const cfg = getCommunityConfig(this.communityStore.currentCommunity)
          this.mapStats = (cfg.data && cfg.data.mapStats) || []
          return
        }
        
        const cfg = getCommunityConfig(this.communityStore.currentCommunity)
        this.mapStats = (cfg.data && cfg.data.mapStats) || []

        this.clearAllMarkers()
        this.loadCommunityBoundary(cfg.boundary)
        // this.loadCommunityLabel(cfg.label) // 不显示小区名称标签
        this.loadBuildings(cfg.buildings)
        this.loadDevices(cfg.devices)
        this.loadVehicles(cfg.vehicles)
        this.loadPersons(cfg.persons)
        this.applyLayerVisibility()
        setTimeout(() => this.flyToCommunity(cfg.view), 200)
      },
      loadBuildings(buildings) {
        if (!this.map || !buildings || !buildings.length) return
        
        // 计算图标大小范围（根据数量动态调整）
        const maxHouses = Math.max(...buildings.map(b => (b.houses || 0)), 1)
        const minSize = 30
        const maxSize = 50
        
        this.buildingMarkers = buildings.map((b) => {
          // 根据房屋数量动态计算图标大小
          const houseCount = b.houses || 0
          const sizeRatio = maxHouses > 0 ? houseCount / maxHouses : 0
          const iconSize = Math.max(minSize, minSize + (maxSize - minSize) * sizeRatio)
          
          // 使用自定义 content 添加呼吸灯动画效果
          const marker = new window.AMap.Marker({
            position: [b.lon, b.lat],
            content: this.getBuildingMarkerContent(iconSize),
            title: b.name || '楼宇',
            anchor: 'bottom-center' // 设置锚点为底部中心
          })
          
          marker.setExtData({
            layerKey: 'building',
            popupInfo: {
              badge: '楼宇',
              title: b.name || '',
              address: b.address,
              buildings: b.buildings,
              houses: b.houses,
              self: b.self,
              rent: b.rent,
              vehicles: b.vehicles,
              image: b.image
            }
          })
          
          marker.on('click', () => {
            this.selectedMarker = marker
            const info = marker.getExtData().popupInfo || {}
            this.openEntityPopup('building', info)
            this.updatePopupPosition(marker)
          })
          
          this.map.add(marker)
          if (this.isLayerVisible('building')) {
            marker.show()
          } else {
            marker.hide()
          }
          
          return marker
        })
      },
      loadCommunityBoundary(boundary) {
        if (!this.map || !boundary || !boundary.length) return
        
        const path = boundary.map((p) => [p.longitude, p.latitude])
        
        this.boundaryPolyline = new window.AMap.Polyline({
          path: path,
          strokeColor: '#00ffff',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          lineJoin: 'round'
        })
        
        this.map.add(this.boundaryPolyline)
      },
      loadCommunityLabel(label) {
        if (!this.map || !label) return
        
        this.communityLabelMarker = new window.AMap.Marker({
          position: [label.longitude, label.latitude],
          content: `<div style="
            background: rgba(15, 30, 50, 0.95);
            color: #00d4ff;
            padding: 6px 12px;
            border-radius: 6px;
            font-weight: bold;
            font-size: 18px;
            white-space: nowrap;
            border: 1px solid rgba(0, 212, 255, 0.5);
            backdrop-filter: blur(10px);
            box-shadow: 0 2px 8px rgba(0, 212, 255, 0.3);
            text-shadow: 0 1px 3px rgba(0, 212, 255, 0.5);
          ">${label.name}</div>`,
          offset: new window.AMap.Pixel(label.offsetX || 0, label.offsetY || 0)
        })
        
        this.map.add(this.communityLabelMarker)
      },
      flyToCommunity(view) {
        if (!this.map || !view) return
        
        // 优先使用边界范围
        if (this.boundaryPolyline) {
          const bounds = this.boundaryPolyline.getBounds()
          if (bounds) {
            // 设置合适的边距，确保能看到完整的边界线
            this.map.setBounds(bounds, false, [60, 60, 60, 60])
            // 限制缩放级别，稍微放大一点
            setTimeout(() => {
              const currentZoom = this.map.getZoom()
              // 如果缩放级别太大（>17），缩小到17
              if (currentZoom > 17) {
                this.map.setZoom(17)
              } else if (currentZoom < 15) {
                // 如果缩放级别太小，适当放大到15
                this.map.setZoom(15)
              }
            }, 100)
            return
          }
        }
        
        // 如果没有边界，使用配置的中心点和缩放级别
        // 高德地图的缩放级别范围是 3-18
        let zoom = 17 // 默认缩放级别，稍微放大一点
        if (view.height) {
          // height 越大，缩放级别越小（地图越远）
          if (view.height > 2000) zoom = 16
          else if (view.height > 1000) zoom = 17
          else if (view.height > 500) zoom = 17
          else zoom = 17
        }
        
        this.map.setCenter([view.longitude || 114.0, view.latitude || 33.0])
        this.map.setZoom(zoom)
      },
      loadDevices(positions) {
        if (!this.map || !positions || !positions.length) return
        
        this.deviceMarkers = positions.map((p) => {
          // 使用自定义 content 添加呼吸灯动画效果
          const marker = new window.AMap.Marker({
            position: [p.lon, p.lat],
            content: this.getDeviceMarkerContent(p.type),
            title: p.name || '设备',
            anchor: 'bottom-center' // 设置锚点为底部中心
          })
          
          const popupInfo = p.type === 'video'
            ? {
                badge: '视频',
                name: '视频监控点',
                status: '在线',
                channels: 4,
                alarms: 0,
                today: 0,
                videoUrl: '',
                address: '河南省驻马店市青云锦绣小区'
              }
            : p.type === 'access'
              ? {
                  badge: '门禁',
                  name: '门禁设备点',
                  status: '在线',
                  passes: 0,
                  abnormal: 0,
                  mode: '常规',
                  address: '河南省驻马店市青云锦绣小区'
                }
              : undefined
          
          marker.setExtData({
            layerKey: p.type,
            popupInfo: popupInfo
          })
          
          marker.on('click', () => {
            this.selectedMarker = marker
            const info = marker.getExtData().popupInfo || {}
            if (p.type === 'video') {
              this.openVideoPlayer(info)
            } else {
              this.openEntityPopup(p.type, info)
              this.updatePopupPosition(marker)
            }
          })
          
          this.map.add(marker)
          if (this.isLayerVisible(p.type)) {
            marker.show()
          } else {
            marker.hide()
          }
          
          return marker
        })
      },
      loadVehicles(vehicles) {
        if (!this.map || !vehicles || !vehicles.length) return
        
        this.vehicleMarkers = vehicles.map((v) => {
          // 使用自定义 content 添加呼吸灯动画效果
          const marker = new window.AMap.Marker({
            position: [v.lon, v.lat],
            content: this.getVehicleMarkerContent(v.status),
            title: v.plate || '车辆',
            anchor: 'bottom-center' // 设置锚点为底部中心
          })
          
          marker.setExtData({
            layerKey: 'vehicle',
            popupInfo: {
              badge: '车辆',
              plate: v.plate,
              status: v.status,
              statusText: v.status === 'moving' ? '行驶中' : '停放中',
              type: '小型车',
              owner: '—',
              io: '—',
              address: '河南省驻马店市青云锦绣小区'
            }
          })
          
          marker.on('click', () => {
            this.selectedMarker = marker
            const info = marker.getExtData().popupInfo || {}
            this.openEntityPopup('vehicle', info)
            this.updatePopupPosition(marker)
          })
          
          this.map.add(marker)
          if (this.isLayerVisible('vehicle')) {
            marker.show()
          } else {
            marker.hide()
          }
          
          return marker
        })
      },
      loadPersons(persons) {
        if (!this.map || !persons || !persons.length) return
        
        this.personMarkers = persons.map((p) => {
          // 使用自定义 content 添加呼吸灯动画效果
          const marker = new window.AMap.Marker({
            position: [p.lon, p.lat],
            content: this.getPersonMarkerContent(p.type),
            title: p.name || '人员',
            anchor: 'bottom-center' // 设置锚点为底部中心
          })
          
          marker.setExtData({
            layerKey: 'person',
            popupInfo: {
              badge: '人员',
              name: p.name,
              type: p.type,
              typeText: p.type === 'visitor' ? '访客' : p.type === 'staff' ? '工作人员' : '居民',
              status: '正常',
              phone: '—',
              lastSeen: '—',
              address: '河南省驻马店市青云锦绣小区'
            }
          })
          
          marker.on('click', () => {
            this.selectedMarker = marker
            const info = marker.getExtData().popupInfo || {}
            this.openEntityPopup('person', info)
            this.updatePopupPosition(marker)
          })
          
          this.map.add(marker)
          if (this.isLayerVisible('person')) {
            marker.show()
          } else {
            marker.hide()
          }
          
          return marker
        })
      },
      // 创建带呼吸灯动画的楼宇标记内容
      getBuildingMarkerContent(size) {
        const iconUrl = this.getBuildingIconImage(size)
        return `<div class="marker-wrapper marker-building">
          <div class="marker-pulse"></div>
          <img src="${iconUrl}" class="marker-icon" style="width: ${size}px; height: ${size}px;" />
        </div>`
      },
      // 创建带呼吸灯动画的设备标记内容
      getDeviceMarkerContent(type) {
        const iconUrl = this.getDeviceIconImage(type)
        const size = 40
        return `<div class="marker-wrapper marker-device marker-${type}">
          <div class="marker-pulse"></div>
          <img src="${iconUrl}" class="marker-icon" style="width: ${size}px; height: ${size}px;" />
        </div>`
      },
      // 创建带呼吸灯动画的车辆标记内容
      getVehicleMarkerContent(status) {
        const iconUrl = this.getVehicleIconImage(status)
        const size = 36
        return `<div class="marker-wrapper marker-vehicle marker-${status}">
          <div class="marker-pulse"></div>
          <img src="${iconUrl}" class="marker-icon" style="width: ${size}px; height: ${size}px;" />
        </div>`
      },
      // 创建带呼吸灯动画的人员标记内容
      getPersonMarkerContent(type) {
        const iconUrl = this.getPersonIconImage(type)
        const size = 32
        return `<div class="marker-wrapper marker-person marker-${type}">
          <div class="marker-pulse"></div>
          <img src="${iconUrl}" class="marker-icon" style="width: ${size}px; height: ${size}px;" />
        </div>`
      },
      // 获取楼宇图标图片（返回 data URL）
      getBuildingIconImage(size) {
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, size, size)
        
        // 绘制外圈
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(10, 25, 41, 0.15)'
        ctx.fill()
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)'
        ctx.lineWidth = 1
        ctx.stroke()
        
        // 绘制楼宇主体
        const buildingWidth = size * 0.35
        const buildingHeight = size * 0.47
        const buildingX = (size - buildingWidth) / 2
        const buildingY = (size - buildingHeight) / 2 - 2
        
        const gradient = ctx.createLinearGradient(buildingX, buildingY, buildingX + buildingWidth, buildingY + buildingHeight)
        gradient.addColorStop(0, 'rgba(0, 212, 255, 0.95)')
        gradient.addColorStop(1, 'rgba(0, 255, 255, 0.6)')
        
        ctx.fillStyle = gradient
        ctx.fillRect(buildingX, buildingY, buildingWidth, buildingHeight)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)'
        ctx.lineWidth = 1
        ctx.strokeRect(buildingX, buildingY, buildingWidth, buildingHeight)
        
        // 绘制窗户
        const windowSize = size * 0.06
        const windowGap = size * 0.09
        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 2; col++) {
            const wx = buildingX + windowGap + col * (windowSize + windowGap)
            const wy = buildingY + windowGap + row * (windowSize + windowGap)
            ctx.fillStyle = `rgba(255, 255, 255, ${0.9 - row * 0.15})`
            ctx.fillRect(wx, wy, windowSize, windowSize)
          }
        }
        
        return canvas.toDataURL('image/png')
      },
      // 获取设备图标图片
      getDeviceIconImage(type) {
        const canvas = document.createElement('canvas')
        const size = 40
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, size, size)
        
        let fillColor = '#ff4444'
        let iconText = '📍'
        if (type === 'video') {
          fillColor = '#ff4444'
          iconText = '📹'
        } else if (type === 'access') {
          fillColor = '#4169E1'
          iconText = '🚪'
        }
        
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = fillColor
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        ctx.font = '20px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(iconText, size / 2, size / 2)
        
        return canvas.toDataURL('image/png')
      },
      // 获取车辆图标图片
      getVehicleIconImage(status) {
        const canvas = document.createElement('canvas')
        const size = 36
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, size, size)
        
        const fillColor = status === 'moving' ? '#4CAF50' : '#FF9800'
        
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = fillColor
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        ctx.font = '20px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('🚗', size / 2, size / 2)
        
        return canvas.toDataURL('image/png')
      },
      // 获取人员图标图片
      getPersonIconImage(type) {
        const canvas = document.createElement('canvas')
        const size = 32
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, size, size)
        
        let fillColor = '#2196F3'
        let iconText = '👤'
        if (type === 'visitor') {
          fillColor = '#FFC107'
          iconText = '🚶'
        } else if (type === 'staff') {
          fillColor = '#9C27B0'
          iconText = '👷'
        }
        
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = fillColor
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        ctx.font = '18px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(iconText, size / 2, size / 2)
        
        return canvas.toDataURL('image/png')
      },
      getDeviceIcon(type) {
        const canvas = document.createElement('canvas')
        const size = 40
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        
        // 清除画布
        ctx.clearRect(0, 0, size, size)
        
        let fillColor = '#ff4444'
        let iconText = '📍'
        if (type === 'video') {
          fillColor = '#ff4444'
          iconText = '📹'
        } else if (type === 'access') {
          fillColor = '#4169E1'
          iconText = '🚪'
        }
        
        // 绘制圆形背景
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = fillColor
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        // 绘制图标文字
        ctx.font = '20px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(iconText, size / 2, size / 2)
        
        // 按照官方文档标准方式创建 Icon
        return new window.AMap.Icon({
          size: new window.AMap.Size(size, size), // 图标显示尺寸
          image: canvas.toDataURL('image/png'), // 图标地址，明确指定格式
          imageSize: new window.AMap.Size(size, size) // 图片大小
        })
      },
      getBuildingIcon() {
        const size = 34
        // 使用 Canvas 绘制楼宇图标，避免 SVG 兼容性问题
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        
        // 清除画布
        ctx.clearRect(0, 0, size, size)
        
        // 绘制外圈
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(10, 25, 41, 0.15)'
        ctx.fill()
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)'
        ctx.lineWidth = 1
        ctx.stroke()
        
        // 绘制楼宇主体
        const buildingWidth = 12
        const buildingHeight = 16
        const buildingX = (size - buildingWidth) / 2
        const buildingY = (size - buildingHeight) / 2 - 2
        
        // 渐变背景
        const gradient = ctx.createLinearGradient(buildingX, buildingY, buildingX + buildingWidth, buildingY + buildingHeight)
        gradient.addColorStop(0, 'rgba(0, 212, 255, 0.95)')
        gradient.addColorStop(1, 'rgba(0, 255, 255, 0.6)')
        
        ctx.fillStyle = gradient
        ctx.fillRect(buildingX, buildingY, buildingWidth, buildingHeight)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)'
        ctx.lineWidth = 1
        ctx.strokeRect(buildingX, buildingY, buildingWidth, buildingHeight)
        
        // 绘制窗户
        const windowSize = 2
        const windowGap = 3
        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 2; col++) {
            const wx = buildingX + windowGap + col * (windowSize + windowGap)
            const wy = buildingY + windowGap + row * (windowSize + windowGap)
            ctx.fillStyle = `rgba(255, 255, 255, ${0.9 - row * 0.15})`
            ctx.fillRect(wx, wy, windowSize, windowSize)
          }
        }
        
        // 按照官方文档标准方式创建 Icon
        return new window.AMap.Icon({
          size: new window.AMap.Size(size, size), // 图标显示尺寸
          image: canvas.toDataURL('image/png'), // 图标地址，明确指定格式
          imageSize: new window.AMap.Size(size, size) // 图片大小
        })
      },
      getVehicleIcon(status) {
        const canvas = document.createElement('canvas')
        const size = 36
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        
        // 清除画布
        ctx.clearRect(0, 0, size, size)
        
        const fillColor = status === 'moving' ? '#4CAF50' : '#FF9800'
        
        // 绘制圆形背景
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = fillColor
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        // 绘制车辆图标
        ctx.font = '20px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('🚗', size / 2, size / 2)
        
        // 按照官方文档标准方式创建 Icon
        return new window.AMap.Icon({
          size: new window.AMap.Size(size, size), // 图标显示尺寸
          image: canvas.toDataURL('image/png'), // 图标地址，明确指定格式
          imageSize: new window.AMap.Size(size, size) // 图片大小
        })
      },
      getPersonIcon(type) {
        const canvas = document.createElement('canvas')
        const size = 32
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        
        // 清除画布
        ctx.clearRect(0, 0, size, size)
        
        let fillColor = '#2196F3'
        let iconText = '👤'
        if (type === 'visitor') {
          fillColor = '#FFC107'
          iconText = '🚶'
        } else if (type === 'staff') {
          fillColor = '#9C27B0'
          iconText = '👷'
        }
        
        // 绘制圆形背景
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
        ctx.fillStyle = fillColor
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        // 绘制人员图标
        ctx.font = '18px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(iconText, size / 2, size / 2)
        
        // 按照官方文档标准方式创建 Icon
        return new window.AMap.Icon({
          size: new window.AMap.Size(size, size), // 图标显示尺寸
          image: canvas.toDataURL('image/png'), // 图标地址，明确指定格式
          imageSize: new window.AMap.Size(size, size) // 图片大小
        })
      },
      isLayerVisible(key) {
        const layer = this.layerList.find((l) => l.key === key)
        if (!layer) return false
        return layer.visible !== false
      },
      toggleLayer() {
        this.applyLayerVisibility()
      },
      applyLayerVisibility() {
        if (!this.map) return
        this.buildingMarkers.forEach((m) => {
          if (this.isLayerVisible('building')) {
            m.show()
          } else {
            m.hide()
          }
        })
        this.deviceMarkers.forEach((m) => {
          const layerKey = m.getExtData().layerKey
          if (this.isLayerVisible(layerKey)) {
            m.show()
          } else {
            m.hide()
          }
        })
        this.vehicleMarkers.forEach((m) => {
          if (this.isLayerVisible('vehicle')) {
            m.show()
          } else {
            m.hide()
          }
        })
        this.personMarkers.forEach((m) => {
          if (this.isLayerVisible('person')) {
            m.show()
          } else {
            m.hide()
          }
        })
      },
      getLayerCount(layerKey) {
        switch (layerKey) {
          case 'building':
            return this.buildingMarkers.length
          case 'video':
            return this.deviceMarkers.filter(m => m.getExtData().layerKey === 'video').length
          case 'access':
            return this.deviceMarkers.filter(m => m.getExtData().layerKey === 'access').length
          case 'vehicle':
            return this.vehicleMarkers.length
          case 'person':
            return this.personMarkers.length
          default:
            return 0
        }
      },
      handleResize() {
        if (this.map) {
          this.map.getSize()
          this.$nextTick(() => {
            this.map.resize()
          })
        }
      },
      updatePopupPosition(marker) {
        if (!this.map || !marker) return
        const position = marker.getPosition()
        const pixel = this.map.lngLatToContainer(position)
        // 弹窗出现在点位右上方
        this.entityPopupPos = { x: Math.round(pixel.x + 12), y: Math.round(pixel.y - 150) }
        
        // 监听地图移动和缩放，更新弹窗位置
        const updatePos = () => {
          if (this.entityPopupVisible && marker) {
            const pos = marker.getPosition()
            const px = this.map.lngLatToContainer(pos)
            this.entityPopupPos = { x: Math.round(px.x + 12), y: Math.round(px.y - 150) }
          }
        }
        
        // 移除之前的监听器（如果存在）
        if (this.mapMoveHandler) {
          this.map.off('moveend', this.mapMoveHandler)
          this.map.off('zoomend', this.mapMoveHandler)
        }
        
        // 添加新的监听器
        this.mapMoveHandler = updatePos
        this.map.on('moveend', updatePos)
        this.map.on('zoomend', updatePos)
      },
      openEntityPopup(layerKey, info) {
        const common = {
          title: info && info.title || '',
          address: info && info.address || '河南省驻马店市青云锦绣小区',
          image: info && info.image,
          badge: info && info.badge || ''
        }

        if (layerKey === 'building') {
          this.entityPopup = {
            ...common,
            badge: common.badge || '楼宇',
            rows: [
              [
                { k: '楼宇', v: `${(info && info.buildings) || 11} 栋` },
                { k: '房屋', v: `${(info && info.houses) || 1232} 户` }
              ],
              [
                { k: '自住', v: `${(info && info.self) || 1001} 户` },
                { k: '租住', v: `${(info && info.rent) || 121} 户` }
              ],
              [{ k: '实有车辆', v: `${(info && info.vehicles) || 873} 辆` }]
            ]
          }
        } else if (layerKey === 'video') {
          this.entityPopup = {
            ...common,
            badge: common.badge || '视频',
            title: (info && info.name) || '视频监控点',
            rows: [
              [
                { k: '在线状态', v: (info && info.status) || '在线' },
                { k: '通道数', v: `${(info && info.channels) || 4}` }
              ],
              [
                { k: '告警', v: `${(info && info.alarms) || 0}` },
                { k: '今日上报', v: `${(info && info.today) || 0}` }
              ]
            ]
          }
        } else if (layerKey === 'access') {
          this.entityPopup = {
            ...common,
            badge: common.badge || '门禁',
            title: (info && info.name) || '门禁设备点',
            rows: [
              [
                { k: '在线状态', v: (info && info.status) || '在线' },
                { k: '今日通行', v: `${(info && info.passes) || 0}` }
              ],
              [
                { k: '异常事件', v: `${(info && info.abnormal) || 0}` },
                { k: '模式', v: (info && info.mode) || '常规' }
              ]
            ]
          }
        } else if (layerKey === 'vehicle') {
          this.entityPopup = {
            ...common,
            badge: common.badge || '车辆',
            title: (info && info.plate) || '车辆',
            enableImagePreview: true,
            rows: [
              [
                { k: '状态', v: (info && info.statusText) || (info && info.status) || '正常' },
                { k: '类型', v: (info && info.type) || '小型车' }
              ],
              [
                { k: '车主', v: (info && info.owner) || '—' },
                { k: '进出', v: (info && info.io) || '—' }
              ]
            ]
          }
        } else if (layerKey === 'person') {
          this.entityPopup = {
            ...common,
            badge: common.badge || '人员',
            title: (info && info.name) || '人员',
            enableImagePreview: true,
            rows: [
              [
                { k: '身份', v: (info && info.typeText) || (info && info.type) || '居民' },
                { k: '状态', v: (info && info.status) || '正常' }
              ],
              [
                { k: '手机号', v: (info && info.phone) || '—' },
                { k: '最近出现', v: (info && info.lastSeen) || '—' }
              ]
            ]
          }
        }

        this.entityPopupVisible = true
      },
      closeEntityPopup() {
        this.entityPopupVisible = false
        this.selectedMarker = null
        // 移除地图移动监听器
        if (this.mapMoveHandler && this.map) {
          this.map.off('moveend', this.mapMoveHandler)
          this.map.off('zoomend', this.mapMoveHandler)
          this.mapMoveHandler = null
        }
      },
      handlePopupThumbClick() {
        if (!this.entityPopupVisible || !this.entityPopup) return
        if (!this.entityPopup.enableImagePreview) return
        this.openImagePreview(this.entityPopup.image, this.entityPopup.title || '图片预览')
      },
      openImagePreview(url, title) {
        this.imagePreviewUrl = url || ''
        this.imagePreviewTitle = title || '图片预览'
        this.imagePreviewVisible = true
      },
      closeImagePreview() {
        this.imagePreviewVisible = false
        this.imagePreviewUrl = ''
      },
      openVideoPlayer(info) {
        this.videoPlayerTitle = (info && info.name) || (info && info.title) || '视频监控'
        this.videoPlayerUrl = (info && info.videoUrl) || ''
        this.videoPlayerVisible = true
      },
      closeVideoPlayer() {
        this.videoPlayerVisible = false
        this.videoPlayerUrl = ''
      },
      toggleVideoFullscreen() {
        const el = this.$refs.videoContainerRef
        if (!el) return
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {})
        } else {
          el.requestFullscreen().catch(() => {})
        }
      }
    }
  }
  </script>

  <style lang="scss" scoped>
  .center-map {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: 1; // 地图在底部
    overflow: visible; // 允许弹窗溢出显示
  }
  .map-stats {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 15px;
    z-index: 100;
    max-width: calc(100% - 40px);
    .stat-card {
      flex: 1;
      width: 190px;
      background: rgba(15, 30, 50, 0.95);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 6px;
      padding: 9px 12px;
      backdrop-filter: blur(10px);
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
      .stat-label {
        font-size: 12px;
        color: #c0d3e8;
        margin-bottom: 6px;
        font-weight: 500;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      }
      .stat-value {
        font-size: 20px;
        font-weight: bold;
        color: #00d4ff;
        margin-bottom: 6px;
        text-shadow: 0 1px 3px rgba(0, 212, 255, 0.5);
        letter-spacing: 0.5px;
      }
      .stat-chart {
        height: 3px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 2px;
        overflow: hidden;
        .chart-bar {
          height: 100%;
          background: linear-gradient(90deg, #00d4ff 0%, #0ff 100%);
          transition: width 0.5s;
        }
      }
    }
  }
  .amap-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
  }
  
  // 修复高德地图图标显示问题
  ::v-deep .amap-icon {
    img {
      position: static !important;
      width: 100% !important;
      height: 100% !important;
      display: block !important;
    }
  }
  
  // 自定义地图控件样式，匹配大屏科技主题
  ::v-deep .amap-controls {
    .amap-toolbar,
    .amap-scale,
    .amap-logo,
    .amap-copyright {
      background: rgba(15, 30, 50, 0.9) !important;
      border: 1px solid rgba(0, 212, 255, 0.3) !important;
      color: #00d4ff !important;
      backdrop-filter: blur(10px);
    }
  }

  .entity-popup {
    position: absolute;
    z-index: 200;
    width: 360px;
    padding: 12px 12px 10px;
    border-radius: 6px;
    background: rgba(15, 30, 50, 0.9);
    border: 1px solid rgba(0, 212, 255, 0.3);
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    color: #fff;
    pointer-events: auto;
  }
  .popup-head {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }

  .video-mask {
    position: absolute;
    inset: 0;
    z-index: 300;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .video-modal {
    width: min(980px, calc(100% - 40px));
    height: min(620px, calc(100% - 40px));
    background: rgba(15, 30, 50, 0.92);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 8px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .video-header {
    height: 44px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  }
  .video-title {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
  }
  .video-actions {
    display: flex;
    gap: 6px;
  }
  .icon-btn {
    width: 32px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid rgba(0, 212, 255, 0.2);
    background: rgba(0, 212, 255, 0.06);
    color: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    line-height: 26px;
    font-size: 16px;
    &:hover {
      background: rgba(0, 212, 255, 0.14);
    }
  }
  .video-body {
    flex: 1;
    position: relative;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .video-el {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }
  .video-empty {
    color: #a0b3c8;
    font-size: 14px;
  }

  .image-mask {
    position: absolute;
    inset: 0;
    z-index: 320;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .image-modal {
    width: 820px;
    height: 520px;
    background: rgba(15, 30, 50, 0.92);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 8px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .image-header {
    height: 44px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  }
  .image-title {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
  }
  .image-body {
    flex: 1;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .image-el {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    background: #000;
  }
  .image-empty {
    color: #a0b3c8;
    font-size: 14px;
  }
  .popup-close {
    position: absolute;
    right: 8px;
    top: 6px;
    width: 28px;
    height: 28px;
    line-height: 28px;
    border: none;
    border-radius: 6px;
    background: rgba(0, 212, 255, 0.06);
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    font-size: 18px;
    &:hover {
      background: rgba(0, 212, 255, 0.14);
    }
  }
  .popup-body {
    display: flex;
    gap: 12px;
  }
  .popup-thumb {
    width: 86px;
    height: 86px;
    border-radius: 6px;
    overflow: hidden;
    background: rgba(0, 212, 255, 0.06);
    border: 1px solid rgba(0, 212, 255, 0.25);
    padding: 0;
    cursor: default;
    appearance: none;
    outline: none;
    &:focus-visible {
      box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.35);
    }
    &.clickable {
      cursor: pointer;
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }
  .thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #a0b3c8;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.18), rgba(0, 255, 255, 0.05));
  }
  .popup-content {
    flex: 1;
    min-width: 0;
  }
  .popup-row {
    display: flex;
    gap: 14px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
  .kv {
    font-size: 13px;
    color: #a0b3c8;
    b {
      color: #ffffff;
      font-weight: 600;
      margin-right: 2px;
    }
  }
  .popup-address {
    margin-top: 6px;
    font-size: 13px;
    color: #a0b3c8;
    text-align: left;
    b {
      color: #ffffff;
      font-weight: 600;
      margin-right: 4px;
    }
  }
  .map-legend {
    position: absolute;
    bottom: 120px;
    left: calc(min(400px, 20vw) + 40px); // 左侧面板宽度 + 更大间距，响应式
    background: rgba(15, 30, 50, 0.9);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 7px;
    padding: 12px 14px;
    backdrop-filter: blur(10px);
    z-index: 200; // 提高层级，确保显示在左侧面板上方
    
    // 响应式调整
    @media (max-width: 1920px) {
      left: calc(min(380px, 20vw) + 40px);
    }
    
    @media (max-width: 1600px) {
      left: calc(min(350px, 22vw) + 40px);
    }
    
    @media (max-width: 1366px) {
      left: calc(min(320px, 24vw) + 40px);
    }
    .legend-item {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
      padding: 5px 8px 5px 10px;
      border-radius: 4px;
      transition:
        background 0.2s,
        box-shadow 0.2s;
      &:last-child {
        margin-bottom: 0;
      }
      .legend-checkbox {
        position: absolute;
        inset: 0 auto auto 0;
        width: 1px;
        height: 1px;
        opacity: 0;
        pointer-events: none;
      }
      .legend-label {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        user-select: none;
      }
      .legend-icon {
        font-size: 18px;
        line-height: 18px;
        width: 18px;
        height: 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        &.video {
          filter: drop-shadow(0 0 2px #ff4444);
        }
        &.access {
          filter: drop-shadow(0 0 2px #4169e1);
        }
        &.vehicle {
          filter: drop-shadow(0 0 2px #ff9800);
        }
        &.person {
          filter: drop-shadow(0 0 2px #2196f3);
        }
        &.building {
          // 确保楼宇图标也统一大小
        }
      }
      .legend-text {
        font-size: 13px;
        color: #a0b3c8;
        flex: 1;
      }
      .legend-count {
        font-size: 12px;
        color: #00d4ff;
        font-weight: bold;
        margin-left: auto;
        padding: 2px 7px;
        background: rgba(0, 212, 255, 0.1);
        border-radius: 3px;
        min-width: 25px;
        text-align: center;
      }
      &.active {
        background: rgba(0, 212, 255, 0.08);
        box-shadow: 0 0 0 1px rgba(0, 212, 255, 0.25) inset;
        .legend-text {
          color: #e7f8ff;
        }
        .legend-count {
          background: rgba(0, 212, 255, 0.2);
        }
      }
    }
  }
  
  // 地图标记呼吸灯动画样式
  ::v-deep .marker-wrapper {
    position: relative;
    display: inline-block;
    .marker-icon {
      position: relative;
      z-index: 2;
      display: block;
    }
    .marker-pulse {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: currentColor;
      opacity: 0;
      z-index: 1;
      animation: pulse 2s ease-out infinite;
    }
    
    // 不同标记类型的颜色
    &.marker-building .marker-pulse {
      color: #00d4ff;
      background: #00d4ff;
    }
    &.marker-device.marker-video .marker-pulse {
      color: #ff4444;
      background: #ff4444;
    }
    &.marker-device.marker-access .marker-pulse {
      color: #4169E1;
      background: #4169E1;
    }
    &.marker-vehicle .marker-pulse {
      color: #4CAF50;
      background: #4CAF50;
    }
    &.marker-person .marker-pulse {
      color: #2196F3;
      background: #2196F3;
    }
  }
  
  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.8;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0.4;
    }
    100% {
      transform: translate(-50%, -50%) scale(2);
      opacity: 0;
    }
  }
  </style>
